import { colors } from "../../../../config/colors";
import { getJobTitleLabel, getJobTitleLabelPlural } from "../../../../utils/jobTitleLabel";

// Helper function to dynamically format time based on data range
const formatTimeDynamically = (minutes) => {
  if (!minutes) return '0m';
  
  // If less than 60 minutes, show in minutes
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }
  
  // If 60 minutes or more, convert to hours and minutes
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
};

// Helper function for Y-axis formatting that shows appropriate units
const formatYAxisTime = (minutes) => {
  if (!minutes) return '0m';
  
  // If less than 60 minutes, show in minutes
  if (minutes < 60) {
    return `${Math.round(minutes)}m`;
  }
  
  // If 60 minutes or more, show in hours with decimal
  const hours = minutes / 60;
  return `${hours.toFixed(1)}h`;
};

// Helper function for tooltip formatting that shows exact values
const formatTooltipTime = (minutes) => {
  if (!minutes) return '0m';
  
  // If less than 60 minutes, show exact minutes with 1 decimal place
  if (minutes < 60) {
    return `${minutes.toFixed(1)}m`;
  }
  
  // If 60 minutes or more, convert to hours and minutes
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  
  if (remainingMinutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMinutes}m`;
};

export function getCharts() {
  const jobLabel = getJobTitleLabel();
  const jobPluralLabel = getJobTitleLabelPlural();
  return [
    {
      name: "jobsPerDay",
      chartType: "line",
      displayName: `${jobPluralLabel} Per Day`,
      subTitle: `Daily ${jobLabel.toLowerCase()} completion trends`,
      index: "date",
      categories: ["jobs"],
      categoryNames: [jobPluralLabel],
      tooltipValueFormatter: (value) => `${value} ${jobPluralLabel.toLowerCase()}`,
      yAxisValueFormatter: (value) => value,
      colors: [colors.filters.set1.primary]
    },
    {
      name: "timePerJob",
      chartType: "line",
      displayName: `Time Per ${jobLabel}`,
      subTitle: `Average time spent on ${jobPluralLabel.toLowerCase()}`,
      index: "date",
      categories: ["timeSpent"],
      categoryNames: ["Time Spent"],
      valueFormatter: (value) => formatTimeDynamically(value),
      tooltipValueFormatter: (value) => formatTooltipTime(value),
      yAxisValueFormatter: (value) => formatYAxisTime(value),
      colors: [colors.filters.set1.primary]
    },
    { 
      name: "timePerTask", 
      chartType: "line",
      displayName: "Time Per Task", 
      subTitle: "Average time spent on tasks", 
      index: "date", 
      categories: ["timeSpent"], 
      categoryNames: ["Time Spent"],
      valueFormatter: (value) => formatTimeDynamically(value),
      tooltipValueFormatter: (value) => formatTooltipTime(value),
      yAxisValueFormatter: (value) => formatYAxisTime(value),
      colors: [colors.filters.set1.primary]
    },
    {
      name: "averageTimePerUnit",
      chartType: "line",
      displayName: "Average Time Per Unit",
      subTitle: "Average time spent per unit",
      index: "date",
      categories: ["timeSpent"],
      categoryNames: ["Time Spent"],
      valueFormatter: (value) => formatTimeDynamically(value),
      tooltipValueFormatter: (value) => formatTooltipTime(value),
      yAxisValueFormatter: (value) => formatYAxisTime(value),
      colors: [colors.filters.set1.primary]
    },
    {
      name: "unitsPerDay",
      chartType: "line",
      displayName: "Units Per Day",
      subTitle: "Daily units processed",
      index: "date",
      categories: ["units"],
      categoryNames: ["Units"],
      tooltipValueFormatter: (value) => `${value} units`,
      yAxisValueFormatter: (value) => value,
      colors: [colors.filters.set1.primary]
    },
    {
      name: "tasksPerVisit",
      chartType: "line",
      displayName: "Tasks Per Visit",
      subTitle: "Number of tasks completed per visit",
      index: "date",
      categories: ["tasks"],
      categoryNames: ["Tasks"],
      tooltipValueFormatter: (value) => `${value} tasks`,
      yAxisValueFormatter: (value) => value.toFixed(1),
      colors: [colors.filters.set1.primary]
    }
  ];
}

export const charts = getCharts();